import { getDevicePref, setDevicePref } from "@eventr/common";
import type { DistanceUnit } from "@eventr/common";
// Type-only: this slice is part of the eagerly-loaded bound store, so keeps maplibre-gl
// out of the entry chunk. Applies to everything this file reaches into under
// home/components/map, too.
import type { LngLat, LngLatBounds } from "maplibre-gl";
import { deviceDetect } from "react-device-detect";
import { StateCreator } from "zustand";
import {
  DEFAULT_FIT_BOUNDS_OPTIONS,
  SEARCH_HIT_STAND_ZOOM,
  SELECTION_ZOOM,
} from "../home/components/map/constants";
import type { LegsSpan } from "../home/components/map/functions/buildItineraryLegs";
import {
  LngLatLabelledAppUser,
  LngLatLabelledItineraryComponents,
} from "../home/components/map/interfaces/locationLabelledObject";
import { StandLocation } from "../home/components/map/interfaces/standLocation";
import { mapDrawerHeight } from "../home/components/map/functions/mapDrawerHeight";
import { getMapRef } from "../home/components/map/mapRef";
import { getDevicePrefName } from "../utils";
import { EphemeralStore } from "./useEphemeralStore";

interface MapState {
  mapIsFullscreen: boolean;
  mapDrawerIsPresented: boolean;
  settingsDrawerIsPresented: boolean;
  selectedLngLatLabelledItineraryComponents: LngLatLabelledItineraryComponents | null;
  selectedLngLatLabelledTeamMember: LngLatLabelledAppUser | null;
  selectedStandLocation: StandLocation | null;
  /** The venue marker, which opens the General section card. */
  generalInfoIsSelected: boolean;
  mapMarkersList: LngLatLabelledItineraryComponents[];
  standMarkersList: StandLocation[];
  mapSearchIsOpen: boolean;
  mapSearchQuery: string;
  /** Hits for the current query. Mirrored from the search index so the camera can size the drawer. */
  mapSearchResultCount: number;
  /** The web tab bar the drawer sits on, measured by the drawer. 0 where no bar is on screen. */
  mapTabBarHeight: number;
  mapViewState: MapViewState;
  mapFocusMode: MapFocusMode;

  // Map filter settings (section/category filtering lives in filterTargets[itinId].map)
  showTeamMembers: boolean;
  showStands: boolean;
  /** Bookable hotels around the selected marker, priced for its stay. */
  showHotels: boolean;
  timeFilter: "upcoming" | "past" | "all";
  fadeLaterEvents: boolean;
  /** The dashed lines between consecutive stops, and the distance on each. */
  showDistances: boolean;
  /** How far the chain of legs runs. See `LegsSpan`. */
  legsSpan: LegsSpan;
  /** What distances are labelled in. Device-wide, so the map reads like the rest of the app. */
  distanceUnit: DistanceUnit;
}

/**
 * What the recentre control frames, in the order its presses cycle through.
 * - `itinerary` — every itinerary item with an address, travel included, but not you. The map
 *   opens on this whenever a different itinerary is selected.
 * - `nearby` — you, plus the itinerary's activities and stands.
 * - `activities` — the activities and stands alone, at no closer than `ACTIVITIES_MAX_ZOOM`.
 */
export type MapFocusMode = "itinerary" | "nearby" | "activities";

interface MapViewState {
  longitude: number;
  latitude: number;
  zoom: number;
}

// Get device name for preferences
const deviceName = getDevicePrefName(deviceDetect);

const getBoolPref = (key: string, defaultValue: "true" | "false") =>
  getDevicePref(key, deviceName, defaultValue) === "true";

// Default map filter settings with device preference fallback
const defaultMapState: MapState = {
  mapIsFullscreen: false,
  mapDrawerIsPresented: false,
  settingsDrawerIsPresented: false,
  selectedLngLatLabelledItineraryComponents: null,
  selectedLngLatLabelledTeamMember: null,
  selectedStandLocation: null,
  generalInfoIsSelected: false,
  mapMarkersList: [],
  standMarkersList: [],
  mapSearchIsOpen: false,
  mapSearchQuery: "",
  mapSearchResultCount: 0,
  mapTabBarHeight: 0,
  mapViewState: {
    longitude: 51,
    latitude: -1,
    zoom: 2,
  },
  mapFocusMode: "itinerary",

  // Default map filter settings - try to load from device preferences first
  showTeamMembers: getBoolPref("map_show_team_members", "true"),
  showStands: getBoolPref("map_show_stands", "true"),
  showHotels: getBoolPref("map_show_hotels", "true"),
  timeFilter: getDevicePref("map_time_filter", deviceName, "all"),
  fadeLaterEvents: getBoolPref("map_fade_later_events", "true"),
  showDistances: getBoolPref("map_show_distances", "true"),
  // A day is a route. Across days you sleep, so the chain restarts.
  legsSpan: getDevicePref("map_legs_span", deviceName, "day") === "itinerary" ? "itinerary" : "day",
  // Matches the hotel distance tags, which are km. Map Settings switches it for the device.
  distanceUnit: getDevicePref("map_distance_unit", deviceName, "km") === "mi" ? "mi" : "km",
};

/**
 * The zoom a selection flies to: the caller's, or else at least `floor`. Already closer than that,
 * the camera stays at the zoom the user chose.
 */
const selectionZoom = (requested: number | undefined, floor: number) =>
  requested ?? Math.max(getMapRef()?.getZoom() ?? 0, floor);

/** Picking anything on the map ends search mode: the drawer body has to show what was picked. */
const CLEARED_SEARCH = { mapSearchIsOpen: false, mapSearchQuery: "" };

interface MapActions {
  closeMapDrawer: () => void;
  selectLngLatLabelledItineraryComponents: (
    lngLatLabelledItineraryComponents: LngLatLabelledItineraryComponents,
    center: LngLat,
    zoom?: number,
  ) => void;
  selectLngLatLabelledTeamMember: (teamMember: LngLatLabelledAppUser, center: LngLat, zoom?: number) => void;
  selectStandLocation: (standLocation: StandLocation, center: LngLat, zoom?: number) => void;
  selectGeneralInfo: (center: LngLat, zoom?: number) => void;
  setMapMarkersList: (list: LngLatLabelledItineraryComponents[]) => void;
  /** Selects the marker holding the itinerary component `itemId`. False when no marker has it. */
  selectItineraryComponentById: (itemId: string) => boolean;
  setStandMarkersList: (list: StandLocation[]) => void;
  openMapSearch: () => void;
  closeMapSearch: () => void;
  setMapSearchResultCount: (count: number) => void;
  setMapTabBarHeight: (height: number) => void;
  setMapSearchQuery: (query: string) => void;
  setMapViewState: (viewState: MapViewState) => void;
  /**
   * `fit` only applies with `bounds`. `maxZoom` caps how close the fit may come, in place of
   * `DEFAULT_FIT_BOUNDS_OPTIONS.maxZoom`; `animate` flies there rather than jumping.
   */
  mapFlyTo: (
    center: LngLat,
    mapDrawerIsPresented: boolean,
    bounds?: LngLatBounds,
    zoom?: number,
    fit?: { maxZoom?: number; animate?: boolean },
  ) => void;
  mapCameraBottomInset: () => number;
  setSettingsDrawerIsPresented: (presented: boolean) => void;

  // Map filter actions
  setShowTeamMembers: (show: boolean) => void;
  setShowStands: (show: boolean) => void;
  setShowHotels: (show: boolean) => void;
  setTimeFilter: (filter: "upcoming" | "past" | "all") => void;
  setFadeLaterEvents: (fade: boolean) => void;
  setShowDistances: (show: boolean) => void;
  setLegsSpan: (span: LegsSpan) => void;
  setDistanceUnit: (unit: DistanceUnit) => void;
}

export interface IMapSlice extends MapState, MapActions {}

export const createMapSlice: StateCreator<EphemeralStore, [], [], IMapSlice> = (set, get) => ({
  ...defaultMapState,
  closeMapDrawer: () => {
    set({
      mapDrawerIsPresented: false,
      selectedLngLatLabelledItineraryComponents: null,
      selectedLngLatLabelledTeamMember: null,
      selectedStandLocation: null,
      generalInfoIsSelected: false,
      ...CLEARED_SEARCH,
    });

    // Only the drawer's padding goes: the camera keeps its zoom and eases what was selected into the
    // middle of the map the drawer gave back, rather than refitting or jumping.
    getMapRef()?.easeTo({ duration: 500, padding: { top: 0, bottom: 0, left: 0, right: 0 } });
  },
  selectLngLatLabelledItineraryComponents: (
    lngLatLabelledItineraryComponents: LngLatLabelledItineraryComponents,
    center,
    zoom,
  ) => {
    set({
      mapDrawerIsPresented: true,
      selectedLngLatLabelledItineraryComponents: lngLatLabelledItineraryComponents,
      selectedLngLatLabelledTeamMember: null,
      selectedStandLocation: null,
      generalInfoIsSelected: false,
      ...CLEARED_SEARCH,
    });
    get().mapFlyTo(center, true, undefined, selectionZoom(zoom, SELECTION_ZOOM));
  },
  selectLngLatLabelledTeamMember: (teamMember, center, zoom) => {
    set({
      mapDrawerIsPresented: true,
      selectedLngLatLabelledItineraryComponents: null,
      selectedLngLatLabelledTeamMember: teamMember,
      selectedStandLocation: null,
      generalInfoIsSelected: false,
      ...CLEARED_SEARCH,
    });
    get().mapFlyTo(center, true, undefined, selectionZoom(zoom, SELECTION_ZOOM));
  },
  selectStandLocation: (standLocation, center, zoom) => {
    set({
      mapDrawerIsPresented: true,
      selectedLngLatLabelledItineraryComponents: null,
      selectedLngLatLabelledTeamMember: null,
      selectedStandLocation: standLocation,
      generalInfoIsSelected: false,
      ...CLEARED_SEARCH,
    });
    get().mapFlyTo(center, true, undefined, selectionZoom(zoom, SEARCH_HIT_STAND_ZOOM));
  },
  selectGeneralInfo: (center, zoom) => {
    set({
      mapDrawerIsPresented: true,
      selectedLngLatLabelledItineraryComponents: null,
      selectedLngLatLabelledTeamMember: null,
      selectedStandLocation: null,
      generalInfoIsSelected: true,
      ...CLEARED_SEARCH,
    });
    get().mapFlyTo(center, true, undefined, selectionZoom(zoom, SELECTION_ZOOM));
  },
  setMapViewState: (viewState: MapViewState) => {
    set({
      mapViewState: viewState,
    });
  },
  mapFlyTo: (center, mapDrawerIsPresented, bounds, zoom, fit) => {
    const mapRef = getMapRef();
    if (mapRef) {
      if (mapRef.getPadding().bottom !== 0 && !mapDrawerIsPresented) {
        /// To reset padding because of the issues with padding
        mapRef.setPadding({
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
        });
      }

      /// Some duplicate code here as zoom and padding cant be set as single values or given as undefined
      if (bounds) {
        const fitOptions = {
          ...DEFAULT_FIT_BOUNDS_OPTIONS,
          // Framing the map does on its own (opening, a new itinerary, a filter) jumps there; only a
          // fit the user asked for flies.
          duration: fit?.animate ? 1000 : 0,
          ...(fit?.maxZoom ? { maxZoom: fit.maxZoom } : {}),
        };
        if (zoom) {
          mapRef.fitBounds(bounds, { ...fitOptions, center: center, zoom: zoom });
        } else {
          // No `center` here: it would replace the one fitBounds works out to allow for the
          // padding, which is taller at the bottom, and push the edge of the bounds off screen.
          mapRef.fitBounds(bounds, fitOptions);
        }
      } else {
        if (mapDrawerIsPresented) {
          mapRef.flyTo({
            duration: 1000,
            center: center,
            zoom: zoom,
            // Centres the target in the map the drawer leaves, not in the canvas: the canvas runs to
            // the true screen edge, and the drawer plus the tab bar under it cover the bottom of it.
            // Taken from the state this action has just set rather than measured, because the drawer
            // has not re-rendered yet — and a fixed fraction of the window was wrong for every
            // drawer height but one.
            padding: {
              bottom: get().mapCameraBottomInset(),
            },
          });
        } else {
          mapRef.flyTo({
            ...DEFAULT_FIT_BOUNDS_OPTIONS,
            duration: 1000,
            center: center,
            zoom: zoom,
          });
        }
      }
    }
  },
  /**
   * How much of the map canvas's bottom edge is covered once the drawer has settled: the drawer
   * itself, plus the tab bar it sits on. The camera pads by this so the thing it flies to ends up in
   * the middle of what is still map.
   */
  mapCameraBottomInset: () => {
    const state = get();

    const { px } = mapDrawerHeight({
      searchIsOpen: state.mapSearchIsOpen,
      searchQuery: state.mapSearchQuery,
      searchResultCount: state.mapSearchResultCount,
      hasSelection: Boolean(
        state.selectedLngLatLabelledItineraryComponents || state.selectedStandLocation || state.generalInfoIsSelected,
      ),
      mapHeight: (getMapRef()?.getContainer().clientHeight ?? window.innerHeight) - state.mapTabBarHeight,
      tabBarHeight: state.mapTabBarHeight,
    });

    return px + state.mapTabBarHeight;
  },
  setSettingsDrawerIsPresented: (presented: boolean) => {
    set({
      settingsDrawerIsPresented: presented,
    });
  },

  // Map filter actions with device preference persistence
  setShowTeamMembers: (show: boolean) => {
    set({ showTeamMembers: show });
    setDevicePref("map_show_team_members", show.toString(), deviceName);
  },
  setShowStands: (show: boolean) => {
    set({ showStands: show });
    setDevicePref("map_show_stands", show.toString(), deviceName);
  },
  setShowHotels: (show: boolean) => {
    set({ showHotels: show });
    setDevicePref("map_show_hotels", show.toString(), deviceName);
  },
  setTimeFilter: (filter: "upcoming" | "past" | "all") => {
    set({ timeFilter: filter });
    setDevicePref("map_time_filter", filter, deviceName);
  },
  setFadeLaterEvents: (fade: boolean) => {
    set({ fadeLaterEvents: fade });
    setDevicePref("map_fade_later_events", fade.toString(), deviceName);
  },
  setShowDistances: (show: boolean) => {
    set({ showDistances: show });
    setDevicePref("map_show_distances", show.toString(), deviceName);
  },
  setLegsSpan: (span: LegsSpan) => {
    set({ legsSpan: span });
    setDevicePref("map_legs_span", span, deviceName);
  },
  setDistanceUnit: (unit: DistanceUnit) => {
    set({ distanceUnit: unit });
    setDevicePref("map_distance_unit", unit, deviceName);
  },
  setMapMarkersList: (list) => {
    set({ mapMarkersList: list });
  },
  selectItineraryComponentById: (itemId) => {
    const marker = get().mapMarkersList.find(({ object }) =>
      [...Object.values(object.present), ...Object.values(object.past)]
        .flat()
        .some((component) => component.data?._id === itemId),
    );
    if (!marker) return false;
    get().selectLngLatLabelledItineraryComponents(marker, marker.lngLat);
    return true;
  },
  setStandMarkersList: (list) => {
    set({ standMarkersList: list });
  },

  // Raises the drawer with it: the search field lives in the drawer header, which is off-screen
  // until something is presented. The camera gets the same bottom padding a selection gives it, so
  // the half of the map still showing is the half above the drawer.
  openMapSearch: () => {
    set({ mapSearchIsOpen: true, mapDrawerIsPresented: true });

    const mapRef = getMapRef();
    if (mapRef) {
      get().mapFlyTo(mapRef.getCenter(), true);
    }
  },
  closeMapSearch: () => {
    const {
      selectedLngLatLabelledItineraryComponents,
      selectedLngLatLabelledTeamMember,
      selectedStandLocation,
      generalInfoIsSelected,
    } = get();

    // Opened from the toolbar there is nothing behind the search to fall back to, so closing it
    // closes the drawer rather than leaving an empty one on screen.
    if (
      !selectedLngLatLabelledItineraryComponents &&
      !selectedLngLatLabelledTeamMember &&
      !selectedStandLocation &&
      !generalInfoIsSelected
    ) {
      get().closeMapDrawer();
      return;
    }

    set(CLEARED_SEARCH);
  },
  setMapSearchQuery: (query) => {
    set({ mapSearchQuery: query });
  },
  setMapSearchResultCount: (count) => {
    set({ mapSearchResultCount: count });
  },
  setMapTabBarHeight: (height) => {
    set({ mapTabBarHeight: height });
  },
});
